import { defineComponent, computed, onMounted, reactive, ref, watch } from '@vue/composition-api'
import { SettingsContext } from '@/utilities/settings-context';
import { alertDialogue, confirmDialogue } from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';

import Pagination from '@/components/pagination/pagination.vue';
import { DateFormat } from '@/filters/date-filter';
import { PromotionType, allPromotions, getPackagePricesForCompany, PromotionPrices, getDefaultPromotionPrices, formatPriceDisplay } from '@/states/promotion-state';
import PromotionTable from '@/components/promotion-table/promotion-table.vue';
import PromotionStrip from '@/components/promotion-strip/promotion-strip.vue';
import { HiddenAxios } from '@/functions/axios-functions';
import { getCityList, getCountryList } from '@/functions/location-function';
import { ModalIconType } from '@/components/system-modals/system-modals';
import AdminCompanyCredits from '@/components/admin-company-credits/admin-company-credits.vue';
import SelectII from '@/components/select-ii/select-ii.vue';
import JsonViewer from '@/components/json-viewer/json-viewer.vue';
import { getId } from '@/functions/authentication-functions';
import { UserTask, Requirement, Location, fromResponse, UserTaskResponseItem } from './admin-manual-sponsored-postings-types';
import { debounce } from 'lodash';
import { City, Country } from '@/types/tags';

type TaskSorting = 'createdAt-desc' | 'createdAt-asc' | 'lastPublished-desc' | 'lastPublished-asc' | 'publishDate-desc' | 'publishDate-asc';

interface ComponentModel {
  pageSize: number;
  currentPage: number;
  loading: boolean;
  loadingTask: boolean;
  failed: boolean;
  processing: boolean;
  search: string;
  chunk: {
    startAt: number;
    endAt: number;
  };

  tasks: UserTask[];
  selectedTask: UserTask | null;
  selectedTaskPackagePrices: PromotionPrices;
  totalPackages: number;
  showPromotionPopup: boolean;
  selectedPromotion: PromotionType;
  showCompleted: boolean;
  onlyAssigned: boolean;
  sorting: TaskSorting;
  cities: City[];
  countries: Country[];
  selectedLocation: null | Location;
  currentAction: Requirement | null;
  detailsTask: UserTask | null;
}

type UserTaskResponse = {
  total: number;
  data: UserTaskResponseItem[];
}

const params = ['size', 'page', 'completed', 'all', 'id', 'action'] as const;

export default defineComponent({
  components: {
    Pagination,
    PromotionTable,
    PromotionStrip,
    AdminCompanyCredits,
    SelectII,
    JsonViewer,
  },
  setup(_, ctx) {
    const model = reactive<ComponentModel>({
      pageSize: 20,
      currentPage: 1,
      loading: false,
      loadingTask: false,
      chunk: { startAt: 0, endAt: 1 },
      tasks: [],
      totalPackages: 0,
      failed: false,
      selectedTask: null,
      selectedTaskPackagePrices: getDefaultPromotionPrices(),
      selectedPromotion: 'none',
      showPromotionPopup: false,
      showCompleted: false,
      onlyAssigned: true,
      sorting: 'createdAt-desc',
      cities: [],
      countries: [],
      selectedLocation: null,
      currentAction: null,
      processing: false,
      search: '',
      detailsTask: null,
    });

    const currentUserId = ref<string | null>('');

    const isQueryChanged = (newQuery: Record<string, string | (string | null)[]>) => {
      const currentQuery = ctx.root.$route.query;
      return params.some(k => currentQuery[k] !== newQuery[k]);
    }

    const queryChanges = (newQuery: Record<string, string | (string | null)[]>) => {
      const currentQuery = ctx.root.$route.query;
      return params.filter(k => currentQuery[k] !== newQuery[k]);
    }

    const setAction = (action: Requirement | null) => {
      model.currentAction = action;

      if (action?.startsWith('JobPosting.Location.')) {
        model.selectedLocation = {
          city: null,
          country: null,
          isRemote: false,
        };
      }
    };

    const updateRouteIfQueryChanged = (newQuery: Record<string, string | (string | null)[]>) => {
      if (isQueryChanged(newQuery)) {
        ctx.root.$router.push({ query: newQuery }).then().catch();
        return true;
      }
      return false;
    }

    const getQuery = () => {
      const query: Record<string, string> = {};

      if (model.pageSize !== 20) query.size = model.pageSize.toString();
      if (model.currentPage !== 1) query.page = model.currentPage.toString();
      if (model.showCompleted) query.completed = model.showCompleted.toString();
      if (!model.onlyAssigned) query.all = (!model.onlyAssigned).toString();
      if (model.selectedTask) query.id = model.selectedTask.id;
      if (model.currentAction) query.action = model.currentAction;
      if (model.search) query.search = model.search;

      return query;
    }

    const getCurrentUserId = () => !currentUserId.value ? currentUserId.value = getId() : currentUserId.value;

    const getPackages = async () => {
      if (model.loading) return;

      model.failed = false;
      model.loading = true;
      model.selectedPromotion = 'none';

      try {
        const response = await HiddenAxios.get<UserTaskResponse>(SettingsContext.getApiUrl('adminGetUserTasks'), {
          params: {
            search: model.search,
            take: model.pageSize,
            offset: model.chunk.startAt,
            hidden: false,
            completed: model.showCompleted,
            onlyAssigned: model.onlyAssigned,
            sort: model.sorting,
          },
        });

        model.tasks = response.data.data.map<UserTask>(j => fromResponse(j));

        model.totalPackages = response.data.total;

      } catch (e) {
        model.failed = true;
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }

      model.loading = false;
    };

    watch(() => [model.pageSize, model.chunk.startAt, model.showCompleted, model.onlyAssigned, model.sorting], async () => {
      await getPackages();
    }, { deep: true });

      // Debounced watcher for model.search
    const debouncedGetPackages = debounce(async () => {
      await getPackages();
    }, 400);
    
    watch(() => model.search, () => {
      debouncedGetPackages();
    });

    const setSelectedTask = async (task: UserTask | null) => {
      model.selectedTask = task;
      model.selectedLocation = null;

      updateRouteIfQueryChanged(getQuery());

      if(task && task.companyId) {
        model.selectedTaskPackagePrices = await getPackagePricesForCompany(task.companyId);
      }
    }

    const reloadTask = async (id: string, setSelected = true) => {
      if (!id || model.loading || model.loadingTask) {
        return;
      }

      model.loadingTask = true;

      try {
        const response = await HiddenAxios.get<UserTaskResponseItem>(SettingsContext.getApiUrl('adminGetUserTask'), {
          params: {
            id
          },
        });

        const index = model.tasks.findIndex(t => t.id === response.data.id);

        const existing = index === -1 ? undefined : model.tasks[index];

        const task = fromResponse(response.data, existing);

        if (index !== -1) {
          model.tasks[index] = { ...task };

          model.tasks = [...model.tasks];
        }

        if (setSelected) {
          setSelectedTask(index === -1 ? task : model.tasks[index]);
        }
      } catch (e) {
        await getPackages();
      }
      model.loadingTask = false;
    }

    watch(() => ctx.root.$route.query, async (val, oldVal) => {
      const changes = queryChanges(oldVal);
      if (!changes.includes('id') && !changes.includes('action')) {
        await getPackages();
        if (model.selectedTask) {
          await reloadTask(model.selectedTask.id);
        }
      }
    }, { deep: true });

    const closeTask = async () => {
      if (model.selectedTask) {
        reloadTask(model.selectedTask.id, false);
      }
      setAction(null);
      model.showPromotionPopup = false;
      model.selectedPromotion = 'none';
      setSelectedTask(null);
      updateRouteIfQueryChanged(getQuery());
    };

    const updateModelFromQuery = () => {
      const query = ctx.root.$route.query;

      if (query.size && parseInt(query.size as string) !== 20) {
        model.pageSize = parseInt(query.size as string);
        model.chunk.endAt = model.pageSize;
      }
      if (query.page && parseInt(query.page as string) !== 1) {
        model.currentPage = parseInt(query.page as string);
        model.chunk.startAt = (model.currentPage - 1) * model.pageSize;
        model.chunk.endAt = model.currentPage * model.pageSize;
      }
      if (query.completed && query.completed === 'true' !== model.showCompleted) {
        model.showCompleted = query.completed === 'true';
      }
      if (query.all && query.all === 'false' !== model.onlyAssigned) {
        model.onlyAssigned = query.all === 'false';
      }
      if(query.search && query.search !== model.search) {
        model.search = query.search as string;
      }
      if (query.id && !model.selectedTask) {
        reloadTask(query.id as string);
      }
      if (query.action && !model.currentAction)
        setAction(query.action as Requirement);
    }

    onMounted(async () => {
      updateModelFromQuery();

      await getPackages();

      if (ctx.root.$route.query.id) {
        await reloadTask(ctx.root.$route.query.id as string);
      }

      model.cities = await getCityList(true);

      model.countries = await getCountryList(true);

      watch(() => [
        model.search,
        model.pageSize,
        model.chunk.startAt,
        model.showCompleted,
        model.onlyAssigned,
        model.currentAction,
        model.selectedTask,
      ], () => {
        updateRouteIfQueryChanged(getQuery());
      }, { deep: true });
    });

    const chunkPackages = (start: number, end: number) => {
      model.chunk.endAt = end;
      model.chunk.startAt = start;
    }

    const promotionSelected = (type: PromotionType) => {
      model.selectedPromotion = type;
    };

    const showPromotionPopup = (job: UserTask) => {
      model.showPromotionPopup = true;
      setSelectedTask(job);
    };

    const promoteExistingJob = async (task: UserTask, type: PromotionType) => {
      model.processing = true;

      try {
        await HiddenAxios.post(SettingsContext.getApiUrl('publishManualWithDates'), {

          id: task.jobPostingId,

          taskId: task.id,

          promote: type,
        });

        model.tasks = model.tasks.filter(j => j.id !== task.id);
        await reloadTask(task.id);
      }
      catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    }

    const createAndPromoteJob = async ({ id }: UserTask, type: PromotionType) => {
      model.processing = true;
      try {
        await HiddenAxios.post(SettingsContext.getApiUrl('adminUserTaskCreateJob'), {

          id,

          promote: type,
        });

        model.tasks = model.tasks.filter(j => j.id !== id);
        await reloadTask(id);
      }
      catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    }

    const promoteJob = async () => {
      const selectedTask = model.selectedTask;

      if (!selectedTask) {
        return;
      }

      if (model.selectedPromotion !== 'none') {
        const found = !selectedTask.canUseCredits ? null : selectedTask.creditAmounts.find(c => c.type == model.selectedPromotion);
        if (!selectedTask.canUseCredits || !found || found.amount === 0) {
          const confirmed = await confirmDialogue(i18n.t('general.confirm'), i18n.t('pages.admin-manual-sponsored-postings.warnings.monetary-promotion'), ModalIconType.warning);

          if (!confirmed)
            return;
        }
      }

      if (selectedTask.jobPostingId) {
        await promoteExistingJob(selectedTask, model.selectedPromotion);
      }
      else {
        await createAndPromoteJob(selectedTask, model.selectedPromotion);
      }
      closeTask();
    };

    const complete = async ({ id }: UserTask) => {
      model.processing = true;
      try {
        const response = await HiddenAxios.post<boolean>(SettingsContext.getApiUrl('adminCompleteUserTask'), { id });

        await reloadTask(id);
      } catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    };

    const ignoreRequirement = async (task: UserTask, requirement: Requirement) => {
      model.processing = true;
      try {
        await HiddenAxios.post(SettingsContext.getApiUrl('adminIgnoreRequirement'), {
          id: task.id,
          requirement,
        });

        await reloadTask(task.id, false);
      } catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    };

    const unIgnoreRequirement = async (task: UserTask, requirement: Requirement) => {
      model.processing = true;
      try {
        await HiddenAxios.post(SettingsContext.getApiUrl('adminUnIgnoreRequirement'), {
          id: task.id,
          requirement,
        });

        await reloadTask(task.id, false);
      } catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    };

    const showCompleted = async (current: boolean) => {
      model.showCompleted = current;
    };

    const notAssigned = async (current: boolean) => {
      model.onlyAssigned = current;
    };

    const setSorting = (sorting: TaskSorting) => {
      model.sorting = sorting;
    };

    const getOfficeJobUrl = (companyId: number, jobPostingId: number) => {
      return `https://recruit.ledigajobb.se/company/${companyId}/jobs/${jobPostingId}`;
    };

    const safeExternalUrl = (value: string | undefined) => {
      if (!value) {
        return null;
      }

      try {
        const url = new URL(value);

        return url.protocol === 'https:' || url.protocol === 'http:'
          ? url.toString()
          : null;
      } catch {
        return null;
      }
    };

    const sortedTasks = computed(() => model.tasks);

    const isLocationValid = (loc: Location | null): loc is Location =>
      !(
        !loc ||
          (!loc.isRemote &&
            loc.city === null &&
            loc.country === null
          ) ||
          (
            loc.country === 'SE' &&
            loc.city === null
          )
        );

    const createJobFromLoc = async () => {
      if (!model.selectedTask || !isLocationValid(model.selectedLocation)) {
        return;
      }

      const selectedLocation = model.selectedLocation;
      const selectedTask = model.selectedTask;

      const location = selectedLocation.isRemote ? "remote" :
        selectedLocation.country === 'SE' ?
          model.cities.find(c => c.id === selectedLocation.city)?.label :
          model.countries.find(c => c.id == selectedLocation.country)?.label;

      const confirmed = await confirmDialogue(i18n.t('general.confirm'), `${i18n.t('pages.admin-manual-sponsored-postings.confirm-create')} <br>${i18n.t('pages.admin-manual-sponsored-postings.ony-time-only')}<br> ${location}`, ModalIconType.warning);

      if (!confirmed) {
        return;
      }

      model.processing = true;
      try {
        const response = await HiddenAxios.post<boolean>(SettingsContext.getApiUrl('adminUserTaskCreateJobsFromLocation'), {
          id: selectedTask.id,
          isRemote: selectedLocation.isRemote,
          location: selectedLocation.country === 'SE' ? selectedLocation.city : selectedLocation.country,
        });

        await reloadTask(selectedTask.id);
      } catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    };

    const createJob = async () => {
      const selectedTask = model.selectedTask;

      if (!selectedTask) {
        return;
      }

      model.processing = true;

      try {
        const response = await HiddenAxios.post<boolean>(SettingsContext.getApiUrl('adminUserTaskCreateJob'), {
          id: selectedTask.id,
        });

        await reloadTask(selectedTask.id);
      } catch (e) {
        alertDialogue(i18n.t('general.error-try-again'), (e as { message: string }).message);
      }
      model.processing = false;
    };

    const setCurrentActions = (task: UserTask, action: Requirement) => {
      setSelectedTask(task);
      setAction(action);
    };

    const isRequiredRequirement = (requirement: Requirement) => {
      return requirement.startsWith('JobPosting.Location.') || requirement.startsWith('Company.Integration.');
    };

    const getSelectedTaskPromotionCreditAmount = (promotion: PromotionType) => {
      if (!model.selectedTask)
        return '';

      if (!model.selectedTask.canUseCredits) {
        return i18n.t('promotion.price', { amount: formatPriceDisplay(model.selectedTaskPackagePrices.monetary[promotion]) });
      }

      const found = model.selectedTask.creditAmounts.find(c => c.type == promotion);

      if (!found || found.amount === 0) {
        return i18n.t('promotion.price', { amount: formatPriceDisplay(model.selectedTaskPackagePrices.monetary[promotion]) });
      }

      return i18n.tc('promotion.price-credit', 1, { amount: formatPriceDisplay(found.amount) })
    };

    const prevTask = (task: UserTask, requirement: Requirement) => {
      if (!task.requirements)
        return;

      const index = task.requirements.indexOf(requirement);

      if (index === -1 || index === 0)
        return null;

      const prev = task.requirements.slice(0, index).reverse().find(r => !task.completedRequirements.includes(r));

      if (prev)
        setCurrentActions(task, prev);
    };

    const nextTask = (task: UserTask, requirement: Requirement) => {
      // get the next non completed requirement relative to the index of requirement
      const index = task.requirements.indexOf(requirement);

      if (index === -1 || index >= task.requirements.length - 1)
        return null;

      const next = task.requirements.slice(index + 1).find(r => !task.completedRequirements.includes(r));

      if (next)
        setCurrentActions(task, next);
    };

    const hasNextTask = (task: UserTask, requirement: Requirement) => {
      if (!task || !task.requirements)
        return false;

      const index = task.requirements.indexOf(requirement);

      if (index === -1 || index >= task.requirements.length - 1)
        return false;

      return task.requirements.slice(index + 1).some(r => !task.completedRequirements.includes(r));
    };

    const hasPrevTask = (task: UserTask, requirement: Requirement) => {
      if (!task || !task.requirements)
        return false;

      const index = task.requirements.indexOf(requirement);

      if (index === -1 || index === 0)
        return false;

      return task.requirements.slice(0, index).reverse().some(r => !task.completedRequirements.includes(r));
    };

    const isCompleted = (task: UserTask, requirement: Requirement) => task.completedRequirements.includes(requirement);

    const showDocumentDetails = (task: UserTask) => {
      model.detailsTask = task;
    };

    const closeDocumentDetails = () => {
      model.detailsTask = null;
    };

    return {
      chunkPackages,
      model,
      getPackages,
      DateFormat,
      getOfficeJobUrl,
      safeExternalUrl,
      promotionSelected,
      showPromotionPopup,
      promoteJob,
      notAssigned,
      createJobFromLoc,
      setCurrentActions,
      closeTask,
      isLocationValid,
      allPromotions,
      complete,
      showCompleted,
      getCurrentUserId,
      createJob,
      isRequiredRequirement,
      getSelectedTaskPromotionCreditAmount,
      prevTask,
      nextTask,
      hasPrevTask,
      hasNextTask,
      isCompleted,
      showDocumentDetails,
      closeDocumentDetails,
      setSorting,
      sortedTasks,
      ignoreRequirement,
      unIgnoreRequirement,
    };
  },
})
